import React, { useState } from 'react';
import { signUpWithEmailAndPassword, signInWithGoogle, signInWithFacebook } from '../../firebase/auth';
import { validateEmailDomain } from '../../firebase/emailValidation';

export default function SignupForm({ onSuccess, onSwitchToLogin, loading, setLoading }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm() || loading) return;

    setLoading(true);
    setErrors({});

    // Verify the email's domain can actually receive mail before creating the
    // account (blocks valid-looking but non-existent domains that bounce).
    const domainCheck = await validateEmailDomain(formData.email.trim());
    if (!domainCheck.valid) {
      const message = domainCheck.reason === 'disposable'
        ? 'Temporary or disposable email addresses are not allowed. Please use a permanent email.'
        : "This email address can't receive mail. Please check for typos or use a different email.";
      setErrors({ email: message });
      setLoading(false);
      return;
    }

    try {
      const displayName = `${formData.firstName.trim()} ${formData.lastName.trim()}`;
      const { user, error } = await signUpWithEmailAndPassword(
        formData.email,
        formData.password,
        displayName
      );

      if (error) {
        let errorMessage = 'Registration failed. Please try again.';

        switch (error.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'An account with this email already exists';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Please enter a valid email address';
            break;
          case 'auth/weak-password':
            errorMessage = 'Password is too weak. Please choose a stronger password.';
            break;
          case 'auth/operation-not-allowed':
            errorMessage = 'Email/password accounts are not enabled';
            break;
          default:
            errorMessage = error.message || errorMessage;
        }

        setErrors({ general: errorMessage });
      } else if (user) {
        // Show verification message instead of immediately proceeding
        setRegisteredEmail(formData.email);
        setShowVerificationMessage(true);
      }
    } catch (error) {
      console.error('Signup error:', error);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (loading) return;

    setLoading(true);
    setErrors({});

    try {
      const { user, error } = await signInWithGoogle();

      if (error) {
        let errorMessage = 'Google sign-in failed. Please try again.';

        switch (error.code) {
          case 'auth/popup-closed-by-user':
            errorMessage = 'Sign-in was cancelled';
            break;
          case 'auth/popup-blocked':
            errorMessage = 'Popup was blocked. Please allow popups and try again.';
            break;
          case 'auth/account-exists-with-different-credential':
            errorMessage = 'An account already exists with this email using a different sign-in method';
            break;
          default:
            errorMessage = error.message || errorMessage;
        }

        setErrors({ general: errorMessage });
      } else if (user) {
        onSuccess();
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookSignIn = async () => {
    if (loading) return;

    setLoading(true);
    setErrors({});

    try {
      const { user, error } = await signInWithFacebook();

      if (error) {
        let errorMessage = 'Facebook sign-in failed. Please try again.';

        switch (error.code) {
          case 'auth/popup-closed-by-user':
            errorMessage = 'Sign-in was cancelled';
            break;
          case 'auth/popup-blocked':
            errorMessage = 'Popup was blocked. Please allow popups and try again.';
            break;
          case 'auth/account-exists-with-different-credential':
            errorMessage = 'An account already exists with this email. Try signing in with a different method.';
            break;
          default:
            errorMessage = error.message || errorMessage;
        }

        setErrors({ general: errorMessage });
      } else if (user) {
        onSuccess();
      }
    } catch (error) {
      console.error('Facebook sign-in error:', error);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (showVerificationMessage) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px', color: 'var(--success)' }}>
          ✅
        </div>
        <h3 style={{ marginBottom: '15px', fontSize: '24px', color: 'var(--text-primary)' }}>
          Account Created Successfully!
        </h3>
        <p style={{ marginBottom: '20px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
          We've sent a verification email to:
        </p>
        <p style={{ marginBottom: '20px', color: 'var(--btn-primary)', fontWeight: 'bold' }}>
          {registeredEmail}
        </p>
        <div style={{
          backgroundColor: 'var(--bg-tertiary)',
          padding: '20px',
          borderRadius: '6px',
          marginBottom: '20px',
          textAlign: 'left'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: 'var(--text-primary)' }}>Next Steps:</h4>
          <ol style={{ margin: 0, paddingLeft: '20px', color: 'var(--text-secondary)', fontSize: '14px' }}>
            <li>Check your email inbox for a verification message</li>
            <li>Click the verification link in the email</li>
            <li>Return to sign in with your verified account</li>
          </ol>
        </div>
        <button
          onClick={onSwitchToLogin}
          style={{
            padding: '12px 24px',
            backgroundColor: 'var(--btn-primary)',
            color: 'var(--btn-text, #fff)',
            border: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          Go to Sign In
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px 30px 30px' }}>
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <h2 style={{
          margin: '0 0 10px 0',
          color: 'var(--text-primary)',
          fontSize: '28px',
          fontWeight: 'bold'
        }}>
          Create Account
        </h2>
        <p style={{
          margin: 0,
          color: 'var(--text-secondary)',
          fontSize: '16px'
        }}>
          Start organizing tournaments today
        </p>
      </div>

      {errors.general && (
        <div style={{
          backgroundColor: 'var(--danger-bg, #fee)',
          border: '1px solid var(--danger-border, #fcc)',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '20px',
          color: 'var(--danger)',
          fontSize: '14px'
        }}>
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <div style={{ flex: 1 }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              fontSize: '14px'
            }}>
              First Name <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                border: `1px solid ${errors.firstName ? 'var(--danger)' : 'var(--border-color)'}`,
                borderRadius: '6px',
                fontSize: '16px',
                outline: 'none',
                transition: 'border-color 0.2s',
                opacity: loading ? 0.7 : 1,
                boxSizing: 'border-box'
              }}
              placeholder="First"
            />
            {errors.firstName && (
              <span style={{ color: 'var(--danger)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.firstName}
              </span>
            )}
          </div>

          <div style={{ flex: 1 }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              fontSize: '14px'
            }}>
              Last Name <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                border: `1px solid ${errors.lastName ? 'var(--danger)' : 'var(--border-color)'}`,
                borderRadius: '6px',
                fontSize: '16px',
                outline: 'none',
                transition: 'border-color 0.2s',
                opacity: loading ? 0.7 : 1,
                boxSizing: 'border-box'
              }}
              placeholder="Last"
            />
            {errors.lastName && (
              <span style={{ color: 'var(--danger)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {errors.lastName}
              </span>
            )}
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 'bold',
            color: 'var(--text-primary)',
            fontSize: '14px'
          }}>
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${errors.email ? 'var(--danger)' : 'var(--border-color)'}`,
              borderRadius: '6px',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s',
              opacity: loading ? 0.7 : 1,
              boxSizing: 'border-box'
            }}
            placeholder="Enter your email"
          />
          {errors.email && (
            <span style={{ color: 'var(--danger)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {errors.email}
            </span>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 'bold',
            color: 'var(--text-primary)',
            fontSize: '14px'
          }}>
            Password
          </label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${errors.password ? 'var(--danger)' : 'var(--border-color)'}`,
              borderRadius: '6px',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s',
              opacity: loading ? 0.7 : 1,
              boxSizing: 'border-box'
            }}
            placeholder="Choose a password"
          />
          {errors.password && (
            <span style={{ color: 'var(--danger)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {errors.password}
            </span>
          )}
        </div>

        <div style={{ marginBottom: '25px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 'bold',
            color: 'var(--text-primary)',
            fontSize: '14px'
          }}>
            Confirm Password
          </label>
          <input
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${errors.confirmPassword ? 'var(--danger)' : 'var(--border-color)'}`,
              borderRadius: '6px',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s',
              opacity: loading ? 0.7 : 1,
              boxSizing: 'border-box'
            }}
            placeholder="Confirm your password"
          />
          {errors.confirmPassword && (
            <span style={{ color: 'var(--danger)', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {errors.confirmPassword}
            </span>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: loading ? 'var(--btn-disabled, #ccc)' : 'var(--success)',
            color: 'var(--btn-text, #fff)',
            border: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: loading ? 'not-allowed' : 'pointer',
            marginBottom: '15px',
            transition: 'background-color 0.2s'
          }}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>

        <div style={{ textAlign: 'center', margin: '20px 0', color: 'var(--text-secondary)' }}>
          <span>or</span>
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: loading ? '#f5f5f5' : '#fff',
            color: '#757575',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: loading ? 'not-allowed' : 'pointer',
            marginBottom: '10px',
            transition: 'background-color 0.2s, box-shadow 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.boxShadow = '0 1px 2px rgba(0,0,0,0.1)';
          }}
        >
          <svg width="20" height="20" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Continue with Google
        </button>

        <button
          type="button"
          onClick={handleFacebookSignIn}
          disabled={loading}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: loading ? '#6d84b4' : '#1877F2',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: loading ? 'not-allowed' : 'pointer',
            marginBottom: '25px',
            transition: 'background-color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          Continue with Facebook
        </button>

        <div style={{ textAlign: 'center' }}>
          <span style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            Already have an account?{' '}
            <button
              type="button"
              onClick={onSwitchToLogin}
              disabled={loading}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--btn-primary)',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                textDecoration: 'underline',
                opacity: loading ? 0.5 : 1
              }}
            >
              Sign in
            </button>
          </span>
        </div>
      </form>
    </div>
  );
}